// Semantic navbar data — full structure ported from the marketing site's
// src/app/components/features/navs/base/GlobalNavData.js (EN copy resolved from
// translations/en/global.json `nav.*`).
//
// Links are stored SEMANTICALLY, never absolutized (see resolve-href.js):
//   - internal: a www.postman.com page (root-relative on the www family,
//     absolute to www elsewhere). `i18n: true` => locale-prefixed when a
//     non-en locale is active on the www family.
//   - external: a full URL whose host is meaningful; passed through unchanged.
//   - section-header items have no link (visual grouping only).
//
// Copy is localized via a `key` on each label/item (see src/i18n/copy.js). The
// `key` is the EXACT global.json dot-path so the generator needs no mapping
// table; the inline `title`/`description` are the English source + fallback.
// NOTE: key strings mirror global.json's convention and must be reconciled
// against the live marketing-site global.json (run `npm run gen:locales`, which
// reports any key it can't find). EN renders correctly regardless.
//
// KEEPING THIS IN SYNC. Until www-next consumes the prerendered navbar (its
// PR #3388, still draft), GlobalNavData.js and this file are two independent
// copies of the same menu and they CAN drift — the Resources > Tools column
// did, silently, because nothing compared them and no test asserted those rows.
// The site owns the menu today, so treat it as the source of truth and diff
// against it rather than editing here from a screenshot:
//
//   1. Fetch the upstream file and its EN copy:
//        gh api "repos/postman-eng/marketing-site-www-next-app/contents/\
//   src/app/components/features/navs/base/GlobalNavData.js?ref=develop" \
//          --jq .content | base64 -d
//        (same for translations/en/global.json)
//   2. Stub its one import (`localizeUrl` -> identity for `en`) and call
//      `getGlobalNavData(t, 'en')` with `t` reading that global.json, so the
//      comparison runs the real upstream code instead of eyeballing a diff.
//   3. Flatten both trees to [column, title, resolved href] and compare in
//      order — resolve ours through `resolveHref` with origin www.postman.com
//      so root-relative and absolute forms are comparable.
//
// `test/nav-data-drift.test.js` pins what that comparison found. It cannot
// detect upstream MOVING, only this file drifting from what was pinned, so
// re-run the diff when the site ships menu changes.

const internal = (path, i18n = true) => ({ type: 'internal', path, i18n });
const external = (url, target) => ({ type: 'external', url, ...(target ? { target } : {}) });

const Product = {
  id: 'product',
  key: 'nav.menus.product',
  title: 'Product',
  dropdown: true,
  columns: [
    {
      key: 'nav.dropdowns.product.platform.title',
      title: 'Platform',
      items: [
        {
          key: 'nav.dropdowns.product.platform.whatsNew',
          title: "What's New",
          link: internal('/new-postman/'),
        },
        {
          key: 'nav.dropdowns.product.platform.security',
          title: 'Security',
          link: internal('/security/'),
        },
        {
          key: 'nav.dropdowns.product.platform.integrations',
          title: 'Integrations',
          link: internal('/product/integrations/'),
        },
        {
          key: 'nav.dropdowns.product.platform.explore',
          title: 'Explore',
          style: 'section-header',
        },
        // Absolute in source (www.postman.com/explore); modeled as internal so
        // it stays relative on the www family. Not localized.
        {
          key: 'nav.dropdowns.product.platform.apiNetwork',
          title: 'Postman API Network',
          link: internal('/explore', false),
        },
        {
          key: 'nav.dropdowns.product.platform.mcpServerCatalog',
          title: 'MCP Server Catalog',
          link: internal('/explore/mcp-servers', false),
        },
        {
          key: 'nav.dropdowns.product.platform.downloadPostman',
          title: 'Download Postman →',
          style: 'app-cta',
          link: internal('/downloads/'),
        },
      ],
    },
    // The three icon columns carry NO descriptions. global.json still holds
    // `<item>.description` strings for them and this file used to render them
    // ("Spec Hub / Manage specifications"), but the live menu dropped them — one
    // line per row, icon + title. Keeping them made every mobile row 52px
    // against the site's 42px, which is what made the two drawers look unrelated.
    {
      key: 'nav.dropdowns.product.designBuild.title',
      title: 'Design & Build',
      items: [
        {
          key: 'nav.dropdowns.product.designBuild.specHub.title',
          title: 'Spec Hub',
          icon: 'https://voyager.postman.com/icon/spec-hub-icon-2.svg',
          link: internal('/product/spec-hub/'),
        },
        {
          key: 'nav.dropdowns.product.designBuild.workspaces.title',
          title: 'Workspaces',
          icon: 'https://voyager.postman.com/icon/workspaces-icon-2.svg',
          link: internal('/product/workspaces/'),
        },
        {
          key: 'nav.dropdowns.product.designBuild.documents.title',
          title: 'Documents',
          icon: 'https://voyager.postman.com/icon/document-icon.svg',
          link: internal('/product/documents/'),
        },
        {
          key: 'nav.dropdowns.product.designBuild.mockServers.title',
          title: 'Mock Servers',
          icon: 'https://voyager.postman.com/icon/local-mock-servers-icon-2.svg',
          link: internal('/product/mock-servers/'),
        },
        {
          key: 'nav.dropdowns.product.designBuild.sdkGenerator.title',
          title: 'SDK Generator',
          icon: 'https://voyager.postman.com/icon/sdk-generator-icon-2.svg',
          link: internal('/product/sdk-generator/'),
        },
        {
          key: 'nav.dropdowns.product.designBuild.flows.title',
          title: 'Flows',
          icon: 'https://voyager.postman.com/icon/flows-local-icon-2.svg',
          link: internal('/product/flows/'),
        },
      ],
    },
    {
      key: 'nav.dropdowns.product.testValidate.title',
      title: 'Test & Validate',
      items: [
        {
          key: 'nav.dropdowns.product.testValidate.apiClient.title',
          title: 'API Client',
          icon: 'https://voyager.postman.com/icon/api-client-2.svg',
          link: internal('/product/api-client/'),
        },
        {
          key: 'nav.dropdowns.product.testValidate.collectionRunner.title',
          title: 'Collection Runner',
          icon: 'https://voyager.postman.com/icon/collection-runner-2.svg',
          link: internal('/product/collection-runner/'),
        },
        {
          key: 'nav.dropdowns.product.testValidate.performanceTesting.title',
          title: 'Performance Testing',
          // No `description`, like its neighbours: global.json carries
          // "Load test at scale" and the source deliberately drops it (MKTG-10852)
          // so this isn't the column's only two-line row.
          icon: 'https://voyager.postman.com/icon/performance-testing-icon-2.svg',
          link: internal('/product/performance-testing/'),
        },
        {
          key: 'nav.dropdowns.product.testValidate.monitors.title',
          title: 'Monitors',
          icon: 'https://voyager.postman.com/icon/monitors-icon-2.svg',
          link: internal('/product/monitors/'),
        },
        {
          // Same key, new English source: global.json now reads "Webhook
          // Testing" (JA: Webhookテスト).
          key: 'nav.dropdowns.product.testValidate.webhooks.title',
          title: 'Webhook Testing',
          icon: 'https://voyager.postman.com/icon/webhooks-icon.svg',
          link: internal('/product/webhooks/'),
        },
        {
          key: 'nav.dropdowns.product.testValidate.functionalTesting.title',
          title: 'Functional Testing',
          // No 15x15 nav-family mark exists for functional testing; this is the
          // 48x48 api-testing illustration the source uses, scaled down.
          icon: 'https://voyager.postman.com/icon/api-testing-icon-postman-2.svg',
          link: internal('/product/functional-testing/'),
        },
        {
          key: 'nav.dropdowns.product.testValidate.dataDrivenTesting.title',
          title: 'Data-Driven Testing',
          // Same gap as functional testing: no 15x15 nav-family mark. The
          // source settles on this 33x32 "run collection with a data file"
          // illustration as the closest semantic match.
          icon: 'https://voyager.postman.com/icon/run-collection-with-data-file-icon.svg',
          link: internal('/product/data-driven-testing/'),
        },
      ],
    },
    {
      key: 'nav.dropdowns.product.manageOperate.title',
      title: 'Manage & Operate',
      items: [
        {
          key: 'nav.dropdowns.product.manageOperate.apiCatalog.title',
          title: 'API Catalog',
          icon: 'https://voyager.postman.com/icon/api-catalog-2.svg',
          link: internal('/product/api-catalog/'),
        },
        {
          key: 'nav.dropdowns.product.manageOperate.insights.title',
          title: 'Insights',
          icon: 'https://voyager.postman.com/icon/insights-icon-2.svg',
          link: internal('/product/postman-insights/'),
        },
        {
          key: 'nav.dropdowns.product.manageOperate.applicationInventory.title',
          title: 'Application Inventory',
          icon: 'https://voyager.postman.com/icon/api-products-icon.svg',
          link: internal('/product/application-inventory/'),
        },
        {
          key: 'nav.dropdowns.product.manageOperate.contextGraph.title',
          title: 'Context Graph',
          // Top-level route, not under /product/ — same shape as Fabric's
          // /fabric-gateway/ page before it moved external (see the brands
          // entry below).
          icon: 'https://voyager.postman.com/icon/api-network.svg',
          link: internal('/context-graph/'),
        },
      ],
    },
  ],
  // Featured "AI cards" row. bgStyle / hoverRing mirror the source's
  // bgClass + hoverBorderClass (GlobalNavData.js) so each card keeps its tint
  // and colored hover outline.
  //
  // The source expresses the tint as Tailwind v4's
  // `bg-linear-to-b from-stone-300/0 to-<color>/20`, which compiles to a
  // TOP-TO-BOTTOM fade ending at 20% alpha. These were 99deg/275deg here — a
  // near-horizontal wash at FULL alpha, so the cards read five times too
  // saturated and tinted the wrong edge.
  //
  // The end colours are the hex Tailwind emits as its own no-color-mix fallback
  // (#ffccfc33 / #ff6c3733 / #fee68533 / #a685ff33), so amber-200 and violet-400
  // are pinned to sRGB rather than left as an OKLCH conversion of ours.
  //
  // `from-stone-300/0` is fully transparent and gradients interpolate
  // premultiplied, so the start colour contributes nothing to the ramp: naming
  // the END hue at 0 alpha renders identically and avoids the source's
  // `in oklab` interpolation hint, which a browser that can't parse it would
  // drop the whole declaration for.
  featured: [
    {
      key: 'nav.dropdowns.product.featured.aiEngineer.title',
      title: 'AI Engineer',
      description: 'Scale velocity with agents. Keep quality and security.',
      icon: 'https://voyager.postman.com/icon/postman-ai-engineer-icon-3.svg',
      link: internal('/product/ai-engineer/'),
      bgStyle:
        'linear-gradient(to bottom, rgba(255, 204, 252, 0) 0%, rgba(255, 204, 252, 0.2) 100%)',
      hoverRing: '#F37FFE',
    },
    {
      key: 'nav.dropdowns.product.featured.agentMode.title',
      title: 'Agent Mode',
      description: 'Automate API workflows with native AI',
      icon: 'https://voyager.postman.com/icon/postman-agent-mode-icon-2.svg',
      link: internal('/product/agent-mode/'),
      bgStyle: 'linear-gradient(to bottom, rgba(255, 108, 55, 0) 0%, rgba(255, 108, 55, 0.2) 100%)',
      hoverRing: '#FF6C37',
    },
    {
      key: 'nav.dropdowns.product.featured.postmanCli.title',
      title: 'Postman CLI',
      description: 'Work with Postman from the command line',
      icon: 'https://voyager.postman.com/icon/postman-cli-icon-2.svg',
      link: internal('/product/postman-cli/'),
      bgStyle:
        'linear-gradient(to bottom, rgba(254, 230, 133, 0) 0%, rgba(254, 230, 133, 0.2) 100%)',
      hoverRing: '#FFA90A',
    },
    {
      key: 'nav.dropdowns.product.featured.postmanMcpServer.title',
      title: 'Postman MCP Server',
      description: 'Give API context to your AI agents',
      icon: 'https://voyager.postman.com/icon/postman-mcp-icon-2.svg',
      link: internal('/product/mcp-server/'),
      bgStyle:
        'linear-gradient(to bottom, rgba(166, 133, 255, 0) 0%, rgba(166, 133, 255, 0.2) 100%)',
      hoverRing: '#7A24F0',
    },
  ],
  // The brands sidebar heading. Was a bare string literal in postman-navbar.js's
  // render, which put it outside collectKeys' walk — so it stayed "More from
  // Postman" under a Japanese menu even though global.json has translated it all
  // along, the same way nav.homeLabel did before v1.0.6.
  brandsHeading: {
    key: 'nav.dropdowns.product.brands.heading',
    title: 'More from Postman',
  },
  brands: [
    {
      key: 'nav.dropdowns.product.brands.fabricGateway.title',
      // Key unchanged; global.json's EN is now just "Fabric".
      title: 'Fabric',
      description: 'Route, govern, and observe your AI traffic',
      icon: 'https://voyager.postman.com/logo/fabric-gateway-mark-gradient.svg',
      // Was internal('/fabric-gateway/') until the source repointed this row at
      // the standalone app ("[DXE-24] chore: update Fabric Gateway nav link to
      // app.fabricgateway.ai", 2026-09-24) — Fabric is its own app now, not a
      // page on www.postman.com.
      link: external('https://app.fabricgateway.ai/'),
    },
    {
      key: 'nav.dropdowns.product.brands.fern.title',
      title: 'Fern',
      description: 'Instantly generate API docs and SDKs',
      icon: 'https://voyager.postman.com/icon/external/fern-docs-leaf-icon.svg',
      link: external('https://buildwithfern.com/'),
    },
    {
      key: 'nav.dropdowns.product.brands.astroAi.title',
      title: 'Astro AI',
      description: 'Manage and control AI agents in production',
      icon: 'https://voyager.postman.com/icon/external/astro-ai-logo-icon.svg',
      link: external('https://astropods.com/'),
    },
    {
      key: 'nav.dropdowns.product.brands.orbit.title',
      title: 'Orbit',
      description: 'Turn any task into the right API calls',
      icon: 'https://voyager.postman.com/logo/external/orbit-icon.svg',
      link: external('https://buildwithorbit.ai/'),
    },
    {
      key: 'nav.dropdowns.product.brands.passport.title',
      title: 'Passport',
      description: 'Secure API access for agents, humans and machines',
      icon: 'https://voyager.postman.com/uploads/postman-passport-logo-icon-2026-09-14-1318.svg',
      link: external('https://app.usepassport.ai/'),
    },
  ],
};

const Solutions = {
  id: 'solutions',
  key: 'nav.menus.solutions',
  title: 'Solutions',
  dropdown: true,
  columnWidth: 'lg',
  columns: [
    {
      title: '',
      items: [
        {
          key: 'nav.dropdowns.solutions.apiGovernance.title',
          title: 'API Governance',
          description: 'Enforce API standards at scale',
          link: internal('/solutions/api-governance/'),
        },
        {
          key: 'nav.dropdowns.solutions.partnerApi.title',
          title: 'Partner API Management',
          description: 'Onboard partners in days, not months',
          link: internal('/solutions/partner-api-management/'),
        },
      ],
    },
    {
      title: '',
      items: [
        {
          key: 'nav.dropdowns.solutions.apiLifecycle.title',
          title: 'API Lifecycle Management',
          description: 'Run every stage as one connected system',
          link: internal('/solutions/api-lifecycle-management/'),
        },
        {
          key: 'nav.dropdowns.solutions.sdlcAutomation.title',
          title: 'SDLC Automation',
          description: 'Embed quality into every delivery stage',
          link: internal('/solutions/sdlc-automation/'),
        },
      ],
    },
  ],
};

const Pricing = {
  id: 'pricing',
  key: 'nav.menus.pricing',
  title: 'Pricing',
  dropdown: false,
  link: internal('/pricing/'),
};

const Enterprise = {
  id: 'enterprise',
  key: 'nav.menus.enterprise',
  title: 'Enterprise',
  dropdown: false,
  link: internal('/postman-enterprise/'),
};

const Resources = {
  id: 'resources',
  key: 'nav.menus.resources',
  title: 'Resources',
  dropdown: true,
  columns: [
    {
      key: 'nav.dropdowns.resources.learn.title',
      title: 'Learn',
      items: [
        {
          key: 'nav.dropdowns.resources.learn.learningHub',
          title: 'Learning Hub',
          link: internal('/learn/'),
        },
        {
          key: 'nav.dropdowns.resources.learn.docs',
          title: 'Docs',
          link: external('https://learning.postman.com'),
        },
        {
          key: 'nav.dropdowns.resources.learn.enterpriseOnboarding',
          title: 'Enterprise Onboarding',
          link: external(
            'https://academy.postman.com/page/enterprise-implementation-onboarding-guide',
            '_blank'
          ),
        },
        {
          key: 'nav.dropdowns.resources.learn.academy',
          title: 'Postman Academy',
          link: external('https://academy.postman.com'),
        },
        {
          key: 'nav.dropdowns.resources.learn.customerStories',
          title: 'Customer Stories',
          link: internal('/customer-stories/'),
        },
        {
          key: 'nav.dropdowns.resources.learn.bestPractices',
          title: 'Postman Best Practices',
          link: internal('/postman-best-practices/'),
        },
      ],
    },
    {
      key: 'nav.dropdowns.resources.connect.title',
      title: 'Connect',
      items: [
        {
          key: 'nav.dropdowns.resources.connect.community',
          title: 'Community',
          link: external('https://community.postman.com'),
        },
        {
          key: 'nav.dropdowns.resources.connect.events',
          title: 'Events',
          link: internal('/events/'),
        },
        {
          key: 'nav.dropdowns.resources.connect.discord',
          title: 'Discord',
          link: external('https://discord.gg/58aNNsRBGR'),
        },
        {
          key: 'nav.dropdowns.resources.connect.mcpServer',
          title: 'Postman MCP Server',
          link: internal('/product/mcp-server/'),
        },
        {
          key: 'nav.dropdowns.resources.connect.partnerProgram',
          title: 'Partner Program',
          link: internal('/partner-program/'),
        },
      ],
    },
    {
      key: 'nav.dropdowns.resources.getSupport.title',
      title: 'Get Support',
      items: [
        {
          key: 'nav.dropdowns.resources.getSupport.supportCenter',
          title: 'Support Center',
          link: external('https://support.postman.com/hc/en-us/'),
        },
        {
          key: 'nav.dropdowns.resources.getSupport.releaseNotes',
          title: 'Release Notes',
          link: internal('/release-notes/'),
        },
        {
          key: 'nav.dropdowns.resources.getSupport.status',
          title: 'Postman Status',
          link: external('https://status.postman.com'),
        },
        {
          key: 'nav.dropdowns.resources.getSupport.security',
          title: 'Security',
          link: internal('/security/'),
        },
      ],
    },
    {
      key: 'nav.dropdowns.resources.postman.title',
      title: 'Postman',
      items: [
        {
          key: 'nav.dropdowns.resources.postman.blog',
          title: 'Blog',
          link: external('https://blog.postman.com'),
        },
        {
          key: 'nav.dropdowns.resources.postman.pressMedia',
          title: 'Press and Media',
          link: internal('/company/press-media/'),
        },
        {
          key: 'nav.dropdowns.resources.postman.aboutPostman',
          title: 'About Postman',
          link: internal('/company/about-postman/'),
        },
      ],
    },
    {
      key: 'nav.dropdowns.resources.tools.title',
      title: 'Tools',
      // Rendered as a grey pill beside the heading, as on the live menu.
      badge: 'new',
      items: [
        // These live in the postman-playground app, served same-origin at /tools
        // but a SEPARATE app from any consumer of this component.
        //
        // Deliberately `external()` with absolute URLs and NO trailing slash,
        // mirroring how www-next expresses them:
        //   - absolute, so a Next consumer treats them as external and doesn't
        //     route them through its own router (`trailingSlash: true` would
        //     otherwise append a slash on client navigation);
        //   - no trailing slash, because the tools app 308-redirects
        //     /tools/<tool>/ back to /tools/<tool>;
        //   - not `internal()`, because these pages aren't in the translated-pages
        //     manifest, so locale-prefixing them would produce dead URLs.
        {
          key: 'nav.dropdowns.resources.tools.specEditor',
          title: 'OpenAPI editor and visualizer',
          link: external('https://www.postman.com/tools/openapi-editor-and-visualizer'),
        },
        {
          key: 'nav.dropdowns.resources.tools.apiDesigner',
          title: 'API designer',
          link: external('https://www.postman.com/tools/api-designer'),
        },
        {
          key: 'nav.dropdowns.resources.tools.unitTestGenerator',
          title: 'AI unit test generator',
          link: external('https://www.postman.com/tools/unit-test-generator'),
        },
        {
          key: 'nav.dropdowns.resources.tools.monitorMultistep',
          title: 'Multi-step API monitor',
          link: external('https://www.postman.com/tools/api-monitor-multistep'),
        },
        {
          key: 'nav.dropdowns.resources.tools.loadTest',
          title: 'API load test',
          link: external('https://www.postman.com/tools/api-load-test'),
        },
        {
          key: 'nav.dropdowns.resources.tools.seeAll',
          title: 'See all tools →',
          style: 'see-all',
          link: external('https://www.postman.com/tools'),
        },
      ],
    },
  ],
};

// Source order: Product, Solutions, Pricing, Enterprise, Resources.
export const navData = [Product, Solutions, Pricing, Enterprise, Resources];
